import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Panel, Button, Icon, SearchInput, CountBadge, FamilyIcon, FamilyTag, KebabButton, DifficultyBars,
  Menu, MenuItem, useToast, useEscape,
} from '../../components/ui';
import { FAMILIES, FAMILY_ORDER, setById } from '../../config/assessmentFamilies';
import { campaignAPI, codingQuestionAPI } from '../../services/api';
import {
  AddQuestionModal, GeneratePreviewModal, regenerateOne, generateOneQuestion, SKILL_AREA_LABELS,
} from '../../components/campaign/GroupedQuestionList';
import {
  CodingQuestionDetailModal, CodingQuestionForm, generateCodingProblems,
} from '../../components/campaign/ExamTab';
import { CODING_LANGUAGE_OPTIONS, codingLanguageLabel, codingLanguagesLabel } from '../../utils/codingLanguages';
import { questionsInSet, setCount, visibleSets, examSetsUsingSet } from './questionSets';
import { languageLabel } from './PositionHeader';

const PAGE = 6;
const LANG_NAMES = { en: 'English', es: 'Spanish' };

/** Python and SQL first; plain language names (no "needs Judge0" / "reviewed" tags). */
const CODING_GEN_OPTIONS = [
  ...CODING_LANGUAGE_OPTIONS.filter((l) => l.id === 'python' || l.id === 'sql'),
  ...CODING_LANGUAGE_OPTIONS.filter((l) => l.id !== 'python' && l.id !== 'sql'),
].map((l) => ({ id: l.id, label: l.label }));

/** "adaptive" is the backend's mixed mode: easy → medium → hard across the batch. */
const GEN_DIFFICULTIES = [
  { id: 'adaptive', label: 'Mixed' },
  { id: 'easy', label: 'Easy' },
  { id: 'medium', label: 'Medium' },
  { id: 'hard', label: 'Hard' },
];
const genDifficultyOptions = (set) => (set?.coding ? GEN_DIFFICULTIES.filter((o) => o.id !== 'adaptive') : GEN_DIFFICULTIES);
const defaultGenDifficulty = (level) => {
  const d = String(level || '').toLowerCase();
  return GEN_DIFFICULTIES.some((o) => o.id === d) ? d : 'medium';
};
/** Mixed difficulty only makes sense for a batch; one question or a coding problem gets medium. */
const singleDifficulty = (d) => (d === 'adaptive' ? 'medium' : d);

const GEN_TYPES = [
  { id: 'mixed', label: 'Mixed' },
  { id: 'technical', label: 'Technical' },
  { id: 'behavioral', label: 'Behavioral' },
];
/** Default and maximum number of questions per generate, by set family. Coding is capped by generateCodingProblems. */
const GEN_COUNT = { jd: { def: 5, max: 20 }, lang: { def: 8, max: 20 }, coding: { def: 3, max: 5 } };
const genCountRule = (set) => GEN_COUNT[set?.coding ? 'coding' : set?.fam === 'lang' ? 'lang' : 'jd'];

/** The generator labels easy questions "basic"; the bank filters on easy. */
const normDifficulty = (d) => {
  const x = String(d || 'medium').toLowerCase();
  return x === 'basic' ? 'easy' : x;
};

const CHANGE_LABEL = { new: 'New', regenerated: 'Regenerated', edited: 'Edited' };

const typeLabel = (t) => {
  const v = String(t || 'technical').toLowerCase();
  return v.charAt(0).toUpperCase() + v.slice(1);
};

const CLASSIC_CODING_LANGS = ['python', 'java', 'javascript', 'cpp', 'csharp', 'vbnet'];

/**
 * Questions is the total to save. Checked programming languages share each programming
 * question. SQL, React, and Angular each take a share of that same total.
 */
function codingQuestionPlan(selected, total) {
  const chosen = selected.length ? selected : ['python'];
  const classic = CLASSIC_CODING_LANGS.filter((id) => chosen.includes(id));
  const groups = [];
  if (classic.length) groups.push({ language: classic[0], languages: classic, weight: classic.length });
  ['sql', 'react', 'angular'].forEach((id) => {
    if (chosen.includes(id)) groups.push({ language: id, languages: [id], weight: 1 });
  });
  if (!groups.length) groups.push({ language: 'python', languages: ['python'], weight: 1 });

  const count = Math.max(1, total);
  const counts = groups.map(() => 0);
  if (count >= groups.length) {
    groups.forEach((_, index) => { counts[index] = 1; });
    const leftover = count - groups.length;
    const weight = groups.reduce((sum, group) => sum + group.weight, 0);
    const shares = groups.map((group) => (leftover * group.weight) / weight);
    const floors = shares.map((share) => Math.floor(share));
    floors.forEach((floor, index) => { counts[index] += floor; });
    let remain = leftover - floors.reduce((sum, floor) => sum + floor, 0);
    const order = shares
      .map((share, index) => ({ index, fraction: share - Math.floor(share) }))
      .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
    for (let step = 0; step < remain; step += 1) counts[order[step].index] += 1;
  } else {
    for (let index = 0; index < count; index += 1) counts[index] = 1;
  }
  return groups
    .map((group, index) => ({ ...group, count: counts[index] }))
    .filter((group) => group.count > 0);
}

/** Add or remove one language. At least one language stays checked. */
function nextCodingLanguages(current, id) {
  const selected = current.length ? current : ['python'];
  if (selected.includes(id)) {
    const next = selected.filter((item) => item !== id);
    return next.length ? next : selected;
  }
  const ids = new Set([...selected, id]);
  return CODING_GEN_OPTIONS.map((option) => option.id).filter((optionId) => ids.has(optionId));
}

/** Language control for coding generation. Several classic languages share one question. */
function CodingLanguagePicker({ value, options, disabled, onToggle }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const selected = value.length ? value : ['python'];
  const label = selected.length === 1
    ? (options.find((option) => option.id === selected[0])?.label || codingLanguageLabel(selected[0]))
    : codingLanguagesLabel(selected);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="wz-lang-pick" ref={boxRef}>
      <button
        type="button"
        className="wz-lang-pick-btn"
        aria-haspopup="true"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        <span>Language</span>
        <span className="wz-lang-pick-value">{label}</span>
        <span aria-hidden="true">{open ? '▴' : '▾'}</span>
      </button>
      {open && (
        <div
          className="wz-lang-menu"
          role="group"
          aria-label="Languages to generate"
          onMouseDown={(event) => event.stopPropagation()}
        >
          {options.map((option) => (
            <label
              key={option.id}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (!disabled) onToggle(option.id);
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(option.id)}
                onChange={() => {}}
                tabIndex={-1}
              />
              {option.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

/** Prototype "Difficulty: All ▾" style filter button. */
function FilterMenu({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <Menu
      open={open}
      onClose={() => setOpen(false)}
      align="left"
      minWidth={180}
      trigger={(
        <Button small aria-haspopup="true" aria-expanded={open} iconAfter="chevD" onClick={() => setOpen((v) => !v)}>
          {label}: {current.label}
        </Button>
      )}
    >
      {options.map((o) => (
        <MenuItem key={o.value} title={o.label} onClick={() => { onChange(o.value); setOpen(false); }} />
      ))}
    </Menu>
  );
}

/** Optional recruiter guidance before regenerating one or more questions. */
function RegenerateDialog({ count, onCancel, onConfirm }) {
  const [comments, setComments] = useState('');
  const boxRef = useRef(null);
  useEscape(onCancel, { ref: boxRef });
  useEffect(() => { boxRef.current?.querySelector('textarea')?.focus(); }, []);
  const go = () => onConfirm(comments.trim());
  return (
    <>
      <div className="wz-scrim" onClick={onCancel} />
      <div className="wz-dialog" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
        <div ref={boxRef} className="wz-dialog-box" role="dialog" aria-modal="true" aria-label="Regenerate question" style={{ width: 'min(480px, 100%)' }}>
          <div className="wz-dialog-head">
            <h2 style={{ fontSize: 18 }}>{count > 1 ? `Regenerate ${count} questions` : 'Regenerate question'}</h2>
            <Button iconOnly icon="x" aria-label="Close" onClick={onCancel} />
          </div>
          <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label className="wz-field">
              <span className="wz-sub">What should the new question focus on? <i>(optional)</i></span>
              <textarea
                rows={4}
                value={comments}
                maxLength={2000}
                onChange={(e) => setComments(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) go(); }}
                placeholder="e.g. Focus on Kubernetes troubleshooting, make it scenario-based, avoid theory questions…"
                style={{ resize: 'vertical', lineHeight: 1.5 }}
              />
            </label>
            <p className="wz-sub" style={{ lineHeight: 1.5 }}>
              The job description, skills and difficulty are always used. Leave this empty to regenerate without extra guidance.
            </p>
          </div>
          <div className="wz-dialog-foot" style={{ justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button need="questions.ai_generate" variant="primary" icon="refresh" onClick={go}>Regenerate</Button>
          </div>
        </div>
      </div>
    </>
  );
}

function RowMenu({ label, items }) {
  const [open, setOpen] = useState(false);
  return (
    <Menu open={open} onClose={() => setOpen(false)} minWidth={170} trigger={<KebabButton label={label} aria-haspopup="true" aria-expanded={open} onClick={() => setOpen((v) => !v)} />}>
      {items.map((it) => (
        <MenuItem key={it.title} title={it.title} danger={it.danger} disabled={it.disabled} onClick={() => { setOpen(false); it.onClick(); }} />
      ))}
    </Menu>
  );
}

/**
 * Prototype viewQuestions(): rail of question sets grouped by family, set header,
 * add menu, generate button, text/coding tables, empty state. All generate/add/
 * edit/save flows are today's handlers (moved from CampaignDetail / GroupedQuestionList).
 */
export default function QuestionBankTab({
  campaign, data, qset, onQsetChange, onOpenInterview, onDirtyChange,
  returnLabel, onQuestionsSaved, onReturn, onCancelReturn,
}) {
  const toast = useToast();
  const savedQs = data.questions;
  const [localQs, setLocalQs] = useState(null); // working copy; null = saved
  const displayed = localQs !== null ? localQs : savedQs;
  const isDirty = localQs !== null;
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const sets = visibleSets(displayed, data.codingQuestions);
  const cur = sets.find((s) => s.id === qset) || setById(qset) || sets[0];
  const [railSearch, setRailSearch] = useState('');
  const [qSearch, setQSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [diffFilter, setDiffFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [codeLangFilter, setCodeLangFilter] = useState('all');
  const [selected, setSelected] = useState(() => new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [gearOpen, setGearOpen] = useState(false);
  const [genLanguage, setGenLanguage] = useState(cur?.language || 'en');
  const [genDifficulty, setGenDifficulty] = useState(() => defaultGenDifficulty(campaign.difficulty_level));
  const [genType, setGenType] = useState('mixed');
  const [genCount, setGenCount] = useState(String(genCountRule(cur).def));
  const countRule = genCountRule(cur);
  const genN = Math.min(countRule.max, Math.max(1, parseInt(genCount, 10) || countRule.def));
  const [genLoading, setGenLoading] = useState(false);
  const [genError, setGenError] = useState('');
  const [savingQs, setSavingQs] = useState(false);
  const [editing, setEditing] = useState(null); // { idx, text }
  const [regeneratingIdx, setRegeneratingIdx] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [addModal, setAddModal] = useState(null); // { examCategory, tab }
  const [previewGen, setPreviewGen] = useState(null); // { examCategory, row }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [codingGenLangs, setCodingGenLangs] = useState(['python']);
  const [codingFormOpen, setCodingFormOpen] = useState(false);
  const [codingDetail, setCodingDetail] = useState(null);
  const [regenFor, setRegenFor] = useState(null); // question indexes awaiting the regenerate dialog

  // Switching set resets per-set view state (not the working copy).
  useEffect(() => {
    setQSearch(''); setShowAll(false); setSelected(new Set()); setDiffFilter('all'); setTypeFilter('all');
    setSourceFilter('all'); setCodeLangFilter('all'); setEditing(null); setAddOpen(false); setCodingFormOpen(false);
    setGenLanguage(cur?.language || 'en');
    setGenCount(String(genCountRule(cur).def));
    if (cur?.coding) setGenDifficulty((d) => singleDifficulty(d));
  }, [cur?.id, cur?.language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the page with unsaved changes asks first.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const setQuestions = useCallback((next) => {
    setLocalQs((prev) => {
      const base = prev !== null ? prev : savedQs;
      return typeof next === 'function' ? next(base) : next;
    });
  }, [savedQs]);

  const totalQuestions = displayed.length + data.codingQuestions.length;

  // Unsaved work, so it can be told apart from what is already saved: a row with no id
  // was never saved (generated, written or picked from the library); a saved row whose
  // text changed was regenerated (row._regen) or edited by hand.
  const savedById = useMemo(
    () => Object.fromEntries((savedQs || []).filter((x) => x.id).map((x) => [x.id, x])),
    [savedQs],
  );
  const rowStatus = (row) => {
    if (!isDirty) return null;
    if (!row.id) return 'new';
    const before = savedById[row.id];
    if (before && before.question !== row.question) return row._regen ? 'regenerated' : 'edited';
    return null;
  };
  const changeCounts = isDirty
    ? displayed.reduce((acc, row) => { const st = rowStatus(row); if (st) acc[st] = (acc[st] || 0) + 1; return acc; }, {})
    : {};
  const usedIn = cur ? examSetsUsingSet(cur, data.examSets) : [];
  const hasSource = displayed.some((q) => q.is_ai_generated !== undefined);

  /* ---------- existing generate / save handlers ---------- */
  const handleGenerateQuestions = async () => {
    setGenLoading(true);
    setGenError('');
    try {
      const res = await campaignAPI.previewQuestions({
        job_role: campaign.job_role || campaign.name,
        job_description: campaign.job_description,
        required_skills: campaign.required_skills || [],
        experience_years: campaign.experience_level ? parseInt(campaign.experience_level, 10) || undefined : undefined,
        num_questions: genN,
        difficulty: genDifficulty,
        question_type: genType,
        language: campaign.language || 'en',
        existing_question_texts: displayed
          .filter((q) => (q.exam_category || 'jd') === 'jd')
          .map((q) => q.question)
          .filter(Boolean),
      });
      const jdRows = (res.data?.questions || []).map((q) => ({
        question: q.question_text || q.question || '',
        type: (q.question_type || 'technical').toLowerCase(),
        difficulty: normDifficulty(q.difficulty),
        exam_category: 'jd',
      }));
      // Generating adds to the set; existing questions are kept. New rows are marked
      // "New" until saved; remove unwanted ones with "Select new" → Delete.
      setLocalQs([...displayed, ...jdRows]);
      setShowAll(true);
      setSelected(new Set());
      toast(jdRows.length < genN
        ? `${jdRows.length} new questions added — the rest were too similar to existing ones`
        : `${jdRows.length} new questions added — review and save`);
    } catch (err) {
      setGenError(err.response?.data?.detail || 'Failed to generate questions. Please try again.');
    } finally {
      setGenLoading(false);
    }
  };

  const handleGenerateProficiencyQuestions = async () => {
    setGenLoading(true);
    setGenError('');
    try {
      const res = await campaignAPI.previewEnglishQuestions({
        job_role: campaign.job_role || campaign.name,
        job_description: campaign.job_description,
        required_skills: campaign.required_skills || [],
        experience_years: campaign.experience_level ? parseInt(campaign.experience_level, 10) || undefined : undefined,
        num_questions: genN,
        difficulty: genDifficulty,
        language: genLanguage,
      });
      const rows = (res.data?.questions || []).map((q) => ({
        question: q.question_text || '',
        type: 'english',
        difficulty: normDifficulty(q.difficulty || genDifficulty),
        exam_category: 'english',
        skill_area: q.skill_area,
        language: genLanguage,
      }));
      setLocalQs([...displayed, ...rows]);
      setShowAll(true);
      if (cur?.language && genLanguage !== cur.language) {
        toast(`${rows.length} ${LANG_NAMES[genLanguage]} questions added — see ${LANG_NAMES[genLanguage]} proficiency`);
      }
    } catch (err) {
      setGenError(err.response?.data?.detail || 'Failed to generate proficiency questions. Please try again.');
    } finally {
      setGenLoading(false);
    }
  };

  const handleGenerateCoding = async () => {
    setGenLoading(true);
    setGenError('');
    try {
      const plan = codingQuestionPlan(codingGenLangs, genN);
      for (const group of plan) {
        await generateCodingProblems(campaign, group.language, {
          difficulty: singleDifficulty(genDifficulty),
          num_questions: group.count,
          languages: group.languages,
        });
      }
      await data.reloadCoding();
      onQuestionsSaved?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Failed to generate coding questions.';
      setGenError(typeof msg === 'string' ? msg : 'Failed to generate coding questions.');
    } finally {
      setGenLoading(false);
    }
  };

  const handleSaveQuestions = async () => {
    const toSave = localQs !== null ? localQs : savedQs;
    setSavingQs(true);
    setGenError('');
    try {
      // _regen is a display-only marker; never sent to the API.
      await campaignAPI.saveQuestions(campaign.id, toSave.map(({ _regen, ...row }) => row));
      // Re-read so new rows carry their real ids — the next save matches rows by id.
      const fresh = await campaignAPI.listQuestions(campaign.id).catch(() => null);
      data.setQuestions(fresh?.data?.questions || toSave);
      setLocalQs(null);
      setSelected(new Set());
      data.reloadCampaign({ quiet: true });
      data.reloadExamSets();
      toast('Questions saved');
      onQuestionsSaved?.();
    } catch (err) {
      setGenError(err.response?.data?.detail || 'Failed to save questions.');
    } finally {
      setSavingQs(false);
    }
  };

  const handleDiscard = () => { setLocalQs(null); setGenError(''); setSelected(new Set()); setEditing(null); };

  /* ---------- row actions (today's GroupedQuestionList behaviour) ---------- */
  const regenerateAt = async (idx, comments) => {
    setRegeneratingIdx(idx);
    setGenError('');
    try {
      const target = displayed[idx];
      const newText = await regenerateOne(target, campaign, displayed.map((x) => x.question), comments);
      setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, question: newText, _regen: true } : q)));
    } catch {
      setGenError('Failed to regenerate question.');
    } finally {
      setRegeneratingIdx(null);
    }
  };

  const deleteAt = (idxs) => {
    const drop = new Set(idxs);
    setQuestions((prev) => prev.filter((_, i) => !drop.has(i)));
    setSelected(new Set());
    setEditing(null);
  };

  const regenerateMany = async (idxList, comments) => {
    const idxs = [...idxList].sort((a, b) => a - b);
    for (let i = 0; i < idxs.length; i += 1) {
      setBulkProgress({ done: i, total: idxs.length });
      // eslint-disable-next-line no-await-in-loop
      await regenerateAt(idxs[i], comments);
    }
    setBulkProgress(null);
  };

  const saveEdit = () => {
    if (!editing) return;
    setQuestions((prev) => prev.map((q, i) => (i === editing.idx ? { ...q, question: editing.text } : q)));
    setEditing(null);
  };

  const generateForPreview = async (examCategory) => {
    setPreviewLoading(true);
    setGenError('');
    try {
      const row = await generateOneQuestion({ campaign, examCategory, questions: displayed, language: genLanguage, difficulty: singleDifficulty(genDifficulty) });
      setPreviewGen({ examCategory, row });
    } catch {
      setGenError('Failed to generate a new question.');
    } finally {
      setPreviewLoading(false);
    }
  };

  /* ---------- coding bank actions ---------- */
  const deleteCoding = async (qs) => {
    const names = qs.length === 1 ? `"${qs[0].title}"` : `${qs.length} problems`;
    if (!window.confirm(`Delete ${names}?`)) return;
    try {
      for (const q of qs) {
        // eslint-disable-next-line no-await-in-loop
        await codingQuestionAPI.remove(campaign.id, q.id);
      }
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Failed to delete question.');
    }
    setSelected(new Set());
    data.reloadCoding();
    data.reloadExamSets();
  };

  /* ---------- rows for the current set ---------- */
  const q = qSearch.trim().toLowerCase();
  const textRows = useMemo(() => {
    if (!cur || cur.coding) return [];
    return questionsInSet(cur, displayed)
      .map((x, n) => ({ ...x, n: n + 1 }))
      .filter(({ q: row }) => diffFilter === 'all' || String(row.difficulty || 'medium').toLowerCase() === diffFilter)
      .filter(({ q: row }) => typeFilter === 'all' || String(row.type || 'technical').toLowerCase() === typeFilter)
      .filter(({ q: row }) => sourceFilter === 'all' || (sourceFilter === 'ai' ? row.is_ai_generated !== false : row.is_ai_generated === false))
      .filter(({ q: row }) => !q || String(row.question || '').toLowerCase().includes(q)
        || String(SKILL_AREA_LABELS[row.skill_area] || row.type || '').toLowerCase().includes(q));
  }, [cur, displayed, diffFilter, typeFilter, sourceFilter, q]);

  const codingRows = useMemo(() => {
    if (!cur?.coding) return [];
    return data.codingQuestions
      .map((row, i) => ({ row, n: i + 1 }))
      .filter(({ row }) => codeLangFilter === 'all' || (row.languages || ['python']).includes(codeLangFilter))
      .filter(({ row }) => !q || `${row.title} ${row.description || ''}`.toLowerCase().includes(q));
  }, [cur, data.codingQuestions, codeLangFilter, q]);

  const allRows = cur?.coding ? codingRows : textRows;
  const shown = showAll || q ? allRows : allRows.slice(0, PAGE);
  const setTotal = cur ? setCount(cur, displayed, data.codingQuestions) : 0;
  const rowKey = (r) => (cur?.coding ? r.row.id : r.idx);
  const selectable = allRows.map(rowKey);
  const allChecked = selectable.length > 0 && selectable.every((k) => selected.has(k));
  const toggle = (k) => setSelected((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(selectable));
  const busy = regeneratingIdx !== null || bulkProgress !== null;

  if (!cur) return null;

  /* ---------- rail ---------- */
  const rq = railSearch.trim().toLowerCase();
  const rail = FAMILY_ORDER.map((fam) => {
    const famSets = sets.filter((s) => s.fam === fam && (!rq || s.name.toLowerCase().includes(rq)));
    if (!famSets.length) return null;
    return (
      <div key={fam} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="wz-rail-group-h"><FamilyIcon fam={fam} size="s" />{FAMILIES[fam].label}</div>
        {famSets.map((s) => {
          const n = setCount(s, displayed, data.codingQuestions);
          return (
            <button key={s.id} type="button" className="wz-rail-item" aria-pressed={s.id === cur.id} onClick={() => onQsetChange(s.id)}>
              <span>{s.name}</span>
              <CountBadge warn={!n}>{n}</CountBadge>
            </button>
          );
        })}
      </div>
    );
  });

  /* ---------- set header ---------- */
  const category = cur.category || 'jd';
  const addMenu = (
    <Menu
      open={addOpen}
      onClose={() => setAddOpen(false)}
      trigger={<Button need="questions.edit" icon="plus" iconAfter="chevD" aria-haspopup="true" aria-expanded={addOpen} onClick={() => setAddOpen((v) => !v)}>Add question</Button>}
    >
      {cur.coding ? (
        <MenuItem title="Write a coding problem" hint="Statement, starter code and test cases" onClick={() => { setAddOpen(false); setCodingFormOpen(true); }} />
      ) : (
        <>
          <MenuItem title="Write or pick a question" hint="Write your own, or reuse one from the question library" onClick={() => { setAddOpen(false); setAddModal({ examCategory: category, tab: 'write' }); }} />
          <MenuItem title="Generate one question" hint="Preview it before it’s added" onClick={() => { setAddOpen(false); generateForPreview(category); }} />
        </>
      )}
    </Menu>
  );

  const runGenerate = cur.coding ? handleGenerateCoding : cur.fam === 'lang' ? handleGenerateProficiencyQuestions : handleGenerateQuestions;
  const genBtn = (
    <div className="wz-gen">
      <label className="wz-gen-field">
        <span>Difficulty</span>
        <select value={genDifficulty} onChange={(e) => setGenDifficulty(e.target.value)} disabled={genLoading}>
          {genDifficultyOptions(cur).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </label>
      {!cur.coding && cur.fam !== 'lang' && (
        <label className="wz-gen-field">
          <span>Type</span>
          <select value={genType} onChange={(e) => setGenType(e.target.value)} disabled={genLoading}>
            {GEN_TYPES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </label>
      )}
      {cur.coding && (
        <CodingLanguagePicker
          value={codingGenLangs}
          options={CODING_GEN_OPTIONS}
          disabled={genLoading}
          onToggle={(id) => setCodingGenLangs((current) => nextCodingLanguages(current, id))}
        />
      )}
      <label className="wz-gen-field" title={`1 to ${countRule.max}`}>
        <span>Questions</span>
        <input
          type="number"
          min={1}
          max={countRule.max}
          value={genCount}
          disabled={genLoading}
          onChange={(e) => setGenCount(e.target.value)}
          onBlur={() => setGenCount(String(genN))}
        />
      </label>
      <Button need="questions.ai_generate" variant="primary" icon="spark" disabled={genLoading} onClick={runGenerate}>
        {genLoading ? 'Generating…' : cur.gen}
      </Button>
    </div>
  );

  /* ---------- body ---------- */
  let body;
  if (!setTotal) {
    body = (
      <Panel className="wz-empty">
        <span className={`wz-fam l ${cur.fam}`} aria-hidden="true"><Icon name="book" sm={false} /></span>
        <h3 style={{ fontSize: 17, fontWeight: 600, marginTop: 4 }}>No questions in this set yet</h3>
        <p>Generate questions from the job description or write your own. Interviews that use this set can’t go live until it has at least one question.</p>
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <Button need="questions.edit" onClick={() => (cur.coding ? setCodingFormOpen(true) : setAddModal({ examCategory: category, tab: 'write' }))}>
            {cur.coding ? 'Write a coding problem' : 'Write a question'}
          </Button>
          {genBtn}
        </div>
      </Panel>
    );
  } else {
    const filters = cur.coding ? (
      <FilterMenu
        label="Language"
        value={codeLangFilter}
        onChange={setCodeLangFilter}
        options={[{ value: 'all', label: 'All' }, ...[...new Set(data.codingQuestions.flatMap((r) => r.languages || ['python']))].map((l) => ({ value: l, label: codingLanguageLabel(l) }))]}
      />
    ) : (
      <>
        <FilterMenu label="Difficulty" value={diffFilter} onChange={setDiffFilter}
          options={[{ value: 'all', label: 'All' }, { value: 'easy', label: 'Easy' }, { value: 'medium', label: 'Medium' }, { value: 'hard', label: 'Hard' }]} />
        {category === 'jd' && (
          <FilterMenu label="Type" value={typeFilter} onChange={setTypeFilter}
            options={[{ value: 'all', label: 'All' }, { value: 'technical', label: 'Technical' }, { value: 'behavioral', label: 'Behavioral' }]} />
        )}
        {hasSource && (
          <FilterMenu label="Source" value={sourceFilter} onChange={setSourceFilter}
            options={[{ value: 'all', label: 'All' }, { value: 'ai', label: 'AI' }, { value: 'manual', label: 'Manual' }]} />
        )}
      </>
    );

    const textHead = (
      <div className={`wz-qrow text${hasSource ? ' src' : ''} th`}>
        <input type="checkbox" aria-label="Select all" checked={allChecked} onChange={toggleAll} />
        <span>#</span><span>Question</span><span>{category === 'jd' ? 'Type' : 'Skill'}</span><span>Difficulty</span>
        {hasSource && <span>Source</span>}<span />
      </div>
    );
    const codeHead = (
      <div className="wz-qrow code th">
        <input type="checkbox" aria-label="Select all" checked={allChecked} onChange={toggleAll} />
        <span>#</span><span>Problem</span><span>Languages</span><span>Test cases</span><span />
      </div>
    );

    const list = cur.coding
      ? shown.map(({ row, n }) => (
        <div className="wz-qrow code" key={row.id}>
          <input type="checkbox" aria-label={`Select problem ${n}`} checked={selected.has(row.id)} onChange={() => toggle(row.id)} />
          <span className="wz-sub">{n}</span>
          <button type="button" className="wz-rowlink" onClick={() => setCodingDetail(row)}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
              <span className="wz-name" style={{ fontWeight: 500 }}>{row.title}</span>
              {row.description && <span className="wz-sub wz-ellipsis">{String(row.description).split('\n')[0]}</span>}
            </span>
          </button>
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(row.languages?.length ? row.languages : ['python']).map((l) => <span key={l} className="wz-lang-pill wz-mono">{codingLanguageLabel(l)}</span>)}
          </span>
          <span style={{ fontSize: 13 }}>{row.visible_test_count ?? 0} visible + {row.hidden_test_count ?? 0} hidden</span>
          <RowMenu label={`Problem ${n} options`} items={[{ title: 'Delete', danger: true, onClick: () => deleteCoding([row]) }]} />
        </div>
      ))
      : shown.map(({ q: row, idx, n }) => {
        const isEditing = editing?.idx === idx;
        return (
          <div className={`wz-qrow text${hasSource ? ' src' : ''}${rowStatus(row) ? ` is-${rowStatus(row)}` : ''}`} key={row.id || `i-${idx}`}>
            <input type="checkbox" aria-label={`Select question ${n}`} checked={selected.has(idx)} onChange={() => toggle(idx)} />
            <span className="wz-sub">{n}</span>
            {isEditing ? (
              <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <textarea
                  aria-label={`Edit question ${n}`}
                  className="wz-textarea"
                  value={editing.text}
                  autoFocus
                  onChange={(e) => setEditing({ idx, text: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(null); } }}
                />
                <span style={{ display: 'flex', gap: 8 }}>
                  <Button need="questions.edit" small variant="primary" onClick={saveEdit}>Save</Button>
                  <Button small onClick={() => setEditing(null)}>Cancel</Button>
                </span>
              </span>
            ) : (
              <span style={{ lineHeight: 1.45, opacity: regeneratingIdx === idx ? 0.5 : 1 }}>
                {rowStatus(row) && regeneratingIdx !== idx && (
                  <span className={`wz-change-tag ${rowStatus(row)}`}>{CHANGE_LABEL[rowStatus(row)]}</span>
                )}
                {regeneratingIdx === idx ? 'Regenerating…' : row.question}
              </span>
            )}
            <span style={{ fontSize: 13, color: 'var(--wz-ink-2)' }}>
              {category === 'jd' ? typeLabel(row.type) : (SKILL_AREA_LABELS[row.skill_area] || 'Proficiency')}
            </span>
            <DifficultyBars level={row.difficulty} />
            {hasSource && (
              <span className="wz-sub" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {row.is_ai_generated !== false && <span style={{ color: 'var(--wz-accent)', display: 'inline-flex' }}><Icon name="spark" /></span>}
                {row.is_ai_generated !== false ? 'AI' : 'Manual'}
              </span>
            )}
            <RowMenu
              label={`Question ${n} options`}
              items={[
                { title: 'Edit', onClick: () => setEditing({ idx, text: row.question }) },
                { title: 'Regenerate', disabled: busy, onClick: () => setRegenFor([idx]) },
                { title: 'Delete', danger: true, onClick: () => deleteAt([idx]) },
              ]}
            />
          </div>
        );
      });

    body = (
      <Panel style={{ overflow: 'visible' }}>
        <div className="wz-qfilters">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {filters}
            {!cur.coding && changeCounts.new > 0 && (
              <Button
                small
                variant="ghost"
                onClick={() => {
                  setShowAll(true);
                  setSelected(new Set(textRows.filter(({ q: row }) => !row.id).map(({ idx }) => idx)));
                }}
              >
                Select new ({textRows.filter(({ q: row }) => !row.id).length})
              </Button>
            )}
          </div>
          <SearchInput label="Search questions" placeholder="Search questions" value={qSearch} onChange={setQSearch} height={32} />
        </div>
        {selected.size > 0 && (
          <div className="wz-selbar" role="region" aria-label="Selected questions">
            <span><b>{selected.size}</b> selected</span>
            {!cur.coding && (
              <Button need="questions.ai_generate" small icon="refresh" disabled={busy} onClick={() => setRegenFor([...selected])}>
                {bulkProgress ? `Regenerating ${bulkProgress.done + 1}/${bulkProgress.total}` : `Regenerate (${selected.size})`}
              </Button>
            )}
            <Button need="questions.edit" small variant="danger" icon="trash" disabled={busy}
              onClick={() => (cur.coding ? deleteCoding(data.codingQuestions.filter((r) => selected.has(r.id))) : deleteAt([...selected]))}>
              Delete
            </Button>
          </div>
        )}
        {cur.coding ? codeHead : textHead}
        {list.length ? list : <p className="wz-sub" style={{ padding: '24px 16px' }}>No questions match “{qSearch}”.</p>}
        {!q && allRows.length > PAGE && (
          <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'center' }}>
            <Button small variant="ghost" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show fewer' : `Show ${allRows.length - PAGE} more`}</Button>
          </div>
        )}
      </Panel>
    );
  }

  return (
    <div className="wz-qb">
      <aside className="wz-panel wz-rail" aria-label="Question sets">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 6px' }}>
          <h2 style={{ fontSize: 15 }}>Question sets</h2>
          <span className="wz-sub">{totalQuestions} questions</span>
        </div>
        <SearchInput label="Find a question set" placeholder="Find a set" value={railSearch} onChange={setRailSearch} />
        {rail}
      </aside>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
        {returnLabel && (
          <div className="wz-return-banner" role="status">
            <span>
              Setting up a <b>{returnLabel}</b>. Generate or add questions and save — you’ll go back to the interview setup automatically.
            </span>
            <span style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <Button small onClick={onCancelReturn}>Dismiss</Button>
              <Button small variant="primary" onClick={onReturn}>Back to interview setup</Button>
            </span>
          </div>
        )}
        <div className="wz-panel wz-set-head">
          <div className="l">
            <FamilyTag fam={cur.fam} />
            <h2 style={{ fontSize: 22, letterSpacing: '-.01em' }}>{cur.name}</h2>
            <p className="wz-sub" style={{ fontSize: 14, lineHeight: 1.5, maxWidth: '62ch' }}>{cur.desc}</p>
            <div className="wz-used">
              <span>{usedIn.length ? 'Used in' : 'Not used in any interview yet'}</span>
              {usedIn.map((es) => (
                <a key={es.id} href="#interview" onClick={(e) => { e.preventDefault(); onOpenInterview(es.id); }}>{es.name}</a>
              ))}
              {cur.fam === 'lang' ? (
                <label style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  Language:
                  <select className="wz-inline-select" value={genLanguage} onChange={(e) => setGenLanguage(e.target.value)} aria-label="Language for generated proficiency questions">
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                  </select>
                </label>
              ) : (
                <span style={{ marginLeft: 8 }}>Language: <b style={{ color: 'var(--wz-ink)', fontWeight: 500 }}>{languageLabel(campaign.language)}</b></span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {cur.coding && (
              <Menu
                open={gearOpen}
                onClose={() => setGearOpen(false)}
                trigger={<Button iconOnly icon="gear" aria-label="Set options" aria-haspopup="true" aria-expanded={gearOpen} onClick={() => setGearOpen((v) => !v)} />}
              >
                <MenuItem title="Generate supporting assets" hint="Write a problem, then generate its test cases and starter code" onClick={() => { setGearOpen(false); setCodingFormOpen(true); }} />
              </Menu>
            )}
            {addMenu}
            {genBtn}
          </div>
        </div>

        {genError && <div className="wz-error" role="alert">{genError}</div>}

        {cur.coding && codingFormOpen && (
          <Panel style={{ padding: 16 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Write a coding problem</h3>
            <CodingQuestionForm
              campaign={campaign}
              onSaved={async () => { setCodingFormOpen(false); await data.reloadCoding(); data.reloadExamSets(); toast('Coding problem added'); onQuestionsSaved?.(); }}
              onCancel={() => setCodingFormOpen(false)}
            />
          </Panel>
        )}

        {body}

        {isDirty && (
          <div className="wz-savebar" role="region" aria-label="Unsaved changes">
            <span style={{ fontWeight: 500 }}>
              Unsaved changes
              {Object.keys(changeCounts).length > 0 && (
                <span className="wz-sub" style={{ fontWeight: 400 }}>
                  {' · '}
                  {['new', 'regenerated', 'edited'].filter((k) => changeCounts[k]).map((k) => `${changeCounts[k]} ${k}`).join(' · ')}
                </span>
              )}
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              <Button onClick={handleDiscard} disabled={savingQs}>Discard</Button>
              <Button need="questions.edit" variant="primary" onClick={handleSaveQuestions} disabled={savingQs || !displayed.length}>{savingQs ? 'Saving…' : 'Save changes'}</Button>
            </span>
          </div>
        )}
      </section>

      {addModal && (
        <AddQuestionModal
          campaign={campaign}
          examCategory={addModal.examCategory}
          initialTab={addModal.tab}
          language={genLanguage}
          questions={displayed}
          setQuestions={setQuestions}
          onClose={() => setAddModal(null)}
        />
      )}
      {(previewGen || (previewLoading && !previewGen)) && (
        <GeneratePreviewModal
          examCategory={previewGen?.examCategory || category}
          row={previewGen?.row}
          loading={previewLoading}
          onCancel={() => { setPreviewGen(null); setPreviewLoading(false); }}
          onRegenerate={() => generateForPreview(previewGen?.examCategory || category)}
          onConfirm={() => { if (previewGen) setQuestions((prev) => [...prev, previewGen.row]); setPreviewGen(null); }}
        />
      )}
      {regenFor && (
        <RegenerateDialog
          count={regenFor.length}
          onCancel={() => setRegenFor(null)}
          onConfirm={(comments) => {
            const idxs = regenFor;
            setRegenFor(null);
            if (idxs.length === 1) regenerateAt(idxs[0], comments);
            else regenerateMany(idxs, comments);
          }}
        />
      )}
      {codingDetail && <CodingQuestionDetailModal campaign={campaign} question={codingDetail} onClose={() => setCodingDetail(null)} />}
    </div>
  );
}
